import { Box, Stack } from "@mui/material";
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from "react-hook-form";

import { MenuItem, Select, Typography } from "@/components/ui-v2";

import { EXPORT_FORM_FIELDS, EXPORT_FORMAT_OPTIONS } from "../../../constants";
import { DimensionField } from "./DimensionField";
import { SizePercentField } from "./SizePercentField";

export const ExportFormModality2dOptions = () => {
  const { getValues, setValue } = useFormContext();

  const handleFormatChange = (value: string, field: ControllerRenderProps) => {
    field.onChange(value);
  };

  const handleSizeChange = (value: string, field: ControllerRenderProps) => {
    field.onChange(value, {
      shouldValidate: true,
    });

    // Automatically update the other fields when one of the fields is changed
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    const type = field.name;
    const formValues = getValues();

    // If the size is changed, update the height and width
    if (type === EXPORT_FORM_FIELDS.EXPORT_SIZE) {
      if (formValues.originalHeight && formValues.originalWidth) {
        const exportHeight = Math.round(
          (formValues.originalHeight ?? 0) * parsedValue,
        );
        const exportWidth = Math.round(
          (formValues.originalWidth ?? 0) * parsedValue,
        );

        setValue(EXPORT_FORM_FIELDS.EXPORT_HEIGHT, exportHeight, {
          shouldValidate: true,
        });
        setValue(EXPORT_FORM_FIELDS.EXPORT_WIDTH, exportWidth, {
          shouldValidate: true,
        });
      }
      return;
    }

    // If the height is changed, update the width and size
    const exportHeight =
      type === EXPORT_FORM_FIELDS.EXPORT_HEIGHT
        ? parsedValue
        : Math.round(
            (parsedValue ?? 0) / (formValues.originalAspectRatio ?? 1),
          );

    // If the width is changed, update the height and size
    const exportWidth =
      type === EXPORT_FORM_FIELDS.EXPORT_WIDTH
        ? parsedValue
        : Math.round(
            (parsedValue ?? 0) * (formValues.originalAspectRatio ?? 1),
          );

    const exportSize =
      formValues.originalWidth && formValues.originalHeight
        ? Math.min(
            Math.max(
              exportWidth / formValues.originalWidth,
              exportHeight / formValues.originalHeight,
              0.1,
            ),
            8,
          )
        : 1;

    setValue(EXPORT_FORM_FIELDS.EXPORT_HEIGHT, exportHeight, {
      shouldValidate: true,
    });
    setValue(EXPORT_FORM_FIELDS.EXPORT_SIZE, exportSize, {
      shouldValidate: true,
    });
    setValue(EXPORT_FORM_FIELDS.EXPORT_WIDTH, exportWidth, {
      shouldValidate: true,
    });
  };

  return (
    <>
      {/* Format Section */}
      <Box>
        <Controller
          name={EXPORT_FORM_FIELDS.FORMAT}
          render={({ field }) => (
            <Select
              {...field}
              fullWidth
              label="Format"
              onChange={(event) =>
                handleFormatChange(event.target.value as string, field)
              }
              size="small"
            >
              {EXPORT_FORMAT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      {/* Image Size Section */}
      <Stack spacing={2} sx={{ maxWidth: 100 }}>
        <Typography variant="small" fontWeight={600}>
          Image Size
        </Typography>
        <DimensionField
          fieldName={EXPORT_FORM_FIELDS.EXPORT_WIDTH}
          label="Width"
          onSizeChange={handleSizeChange}
        />
        <DimensionField
          fieldName={EXPORT_FORM_FIELDS.EXPORT_HEIGHT}
          label="Height"
          onSizeChange={handleSizeChange}
        />
        <SizePercentField onSizeChange={handleSizeChange} />
      </Stack>
    </>
  );
};
